<script lang="ts" setup>
import { computed, ref } from "vue";
import BaseChart, {
  type ChartData,
  type ChartOptions,
} from "@/views/shared/components/BaseChart.vue";
import DashboardHeader from "@/components/DashboardHeader.vue";
import { useDashboardSummary } from "@/queries/dashboard";
import { paletteAt } from "./chartPalette";

// Execution volume over time, per workflow. Range is "auto": the backend fits the bucket
// granularity (hourly/daily/weekly/monthly) to however much history exists, so the line is never
// a flat row of empty months.
const range = ref("auto");
const { summary, summaryLoading, summaryError } = useDashboardSummary(range);

const usage = computed(() => summary.value.usage);
const hasData = computed(() => usage.value.buckets.length > 0 && usage.value.series.length > 0);

const granularityLabel = computed(() => {
  switch (usage.value.granularity) {
    case "hourly":
      return "hourly";
    case "weekly":
      return "weekly";
    case "monthly":
      return "monthly";
    default:
      return "daily";
  }
});

// Stacked area, one band per workflow. Stacking (rather than overlapping lines) is what makes the
// lower-volume workflows visible — with sparse run data each series is near-zero, so as plain
// lines they'd collapse onto the x-axis and read as a single line.
const chartData = computed<ChartData>(() => ({
  labels: usage.value.buckets.map((b) => b.label),
  datasets: usage.value.series.map((s, i) => ({
    label: s.name,
    data: s.data,
    borderColor: paletteAt(i).border,
    backgroundColor: paletteAt(i).bg,
    borderWidth: 1,
    fill: true,
  })),
}));

const chartOptions = computed<ChartOptions>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: "top",
      labels: { usePointStyle: true, pointStyle: "circle", boxWidth: 6, boxHeight: 6 },
    },
    title: { display: false },
    tooltip: { mode: "index", intersect: false },
  },
  scales: {
    x: { stacked: true, grid: { display: true } },
    y: { stacked: true, beginAtZero: true, grid: { display: true } },
  },
  elements: {
    line: { tension: 0.3 },
    point: { radius: 0, hoverRadius: 6 },
  },
}));
</script>

<template>
  <DashboardHeader :title="'Usage'">
    <template #actions>
      <span v-if="hasData" class="text-muted small align-self-center">{{ granularityLabel }}</span>
    </template>
  </DashboardHeader>

  <div style="height: 300px; position: relative">
    <div v-if="summaryLoading && !hasData" class="text-muted py-5 text-center">
      Loading…
    </div>
    <div v-else-if="summaryError" class="text-danger py-5 text-center">
      Couldn't load usage.
    </div>
    <div v-else-if="!hasData" class="text-muted py-5 text-center">
      No execution history yet.
    </div>
    <BaseChart
      v-else
      type="line"
      :data="chartData"
      :options="chartOptions"
      :height="300"
    />
  </div>
</template>

<style scoped lang="scss"></style>
